import { useState } from 'react';
import { Select } from '../../../components/Select/Select';
import { useConfig } from '../../../config';
import { Updater } from '../../../hooks/useImmer';
import { useTranslation } from '../../../i18n/i18n';
import { ApiSchedule } from '../../../types';
import { Value, isDate } from '../../Calendar/Calendar';
import { CalendarSelect } from '../../CalendarSelect/CalendarSelect';
import { applySuggestion, endDateFor } from '../applySuggestion';
import {
	DEFAULT_SHIP_IN_HOURS,
	formatCalendarDate,
	formatScheduleText,
	getScheduleSuggestions,
	isSwedishHoliday,
	isWeekend,
} from '../utils';
import { TileArgs } from 'react-calendar';

interface SelectSuggestionsProps {
	schedule: ApiSchedule;
	setSchedule: Updater<ApiSchedule>;
	setShowAdvanced: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SelectSuggestions = ({ schedule, setSchedule, setShowAdvanced }: SelectSuggestionsProps): JSX.Element => {
	const t = useTranslation().subscriptions;
	const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>();
	const { locale } = useConfig();

	const renderValue = (s: ApiSchedule) => formatScheduleText({ ...s, startDate: schedule?.startDate }, locale, t);

	const findSchedule = (query: string, sl: ApiSchedule[]) => sl.find((s) => s.id.toString() === query);

	const onChangeStartDate = (value: Value): void => {
		if (!value || !isDate(value)) return;

		setSchedule((draft) => {
			draft.startDate = formatCalendarDate(value);
			// this screen has no end-date control, so a date left behind it has to be dropped here
			draft.endDate = endDateFor(draft.startDate, draft.endDate);
		});
	};

	const checkDisabled = ({ date, view }: TileArgs): boolean => {
		//TODO: Make method calculate changing dates of easter using gauss algorithm
		const twoDaysAhead = new Date(new Date().setHours(new Date().getHours() + DEFAULT_SHIP_IN_HOURS));

		return view === 'month' && (isWeekend(date) || isSwedishHoliday(date) || twoDaysAhead > date);
	};

	const selectSuggestion = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if ((e.target as HTMLElement).id === 'null') return setShowAdvanced(true);

		if (schedule.startDate) {
			const id = (e.target as HTMLElement).id;
			const searchSchedule = findSchedule(id, getScheduleSuggestions(schedule.startDate, schedule.productListId));

			setSelectedSuggestion(id);

			if (searchSchedule) {
				setSchedule((draft) => applySuggestion(draft, searchSchedule));
			}
		}
	};

	return (
		<>
			<p>{t.schedule.whenShouldTheFirstOrderBeSent}</p>
			<CalendarSelect
				onChangeDate={onChangeStartDate}
				dateValue={schedule?.startDate?.toString()}
				checkDisabled={checkDisabled}
			/>
			<p>{t.schedule.howOftenShouldYourOrderBeSent}</p>
			{schedule?.startDate && (
				<Select
					className="set-subscription-select"
					size="large"
					value={selectedSuggestion}
					onSelect={selectSuggestion}
					nullValue={t.schedule.customize}
					searchValue={findSchedule}
					renderValue={renderValue}
					placeholder={t.schedule.chooseSchedule}
					list={getScheduleSuggestions(schedule.startDate, schedule.productListId)}
					idField="id"
				/>
			)}
		</>
	);
};
